import { ChangeEvent, FC, useState } from 'react'
import classNames from 'classnames'

import { FormDatePicker } from '@/components'
import { PreviousAndNextButton } from '@/compositions/@buttons/PreviousAndNextButton/PreviousAndNextButton'
import { validateVerifyRegistrationData } from '@/compositions/RegistrationFlow/helpers/validateFormData'
import { UserFormData } from '@/entities/User/User'
import { isObjEmpty } from '@/helpers/tools'
import { trans } from '@/helpers/trans'

import './VerifyRegistration.scss'


interface VerifyRegistrationProps {
    values: UserFormData
    onChange: (formData: Partial<UserFormData>) => void
    onPreviousStep: () => void
    onNextStep: () => void
    className?: string
    inputClassName?: string
}

export const VerifyRegistration: FC<VerifyRegistrationProps> = ({
    values,
    onChange,
    onPreviousStep,
    onNextStep,
    className = '',
    inputClassName = '',
}) => {
    const [submitted, setSubmitted] = useState(false)
    const validationErrors = validateVerifyRegistrationData(values, submitted)

    const onNextStepClick = () => {
        setSubmitted(true)
        if (isObjEmpty(validateVerifyRegistrationData(values, true))) {
            onNextStep()
        }
    }

    const onChangeFirstname = (e: ChangeEvent<HTMLInputElement>) => {
        onChange({ firstname: e.target.value })
    }

    const onChangeAffix = (e: ChangeEvent<HTMLInputElement>) => {
        onChange({ affix: e.target.value })
    }

    const onChangeLastname = (e: ChangeEvent<HTMLInputElement>) => {
        onChange({ lastname: e.target.value })
    }

    const onChangePhone = (e: ChangeEvent<HTMLInputElement>) => {
        onChange({ phone: e.target.value })
    }

    const onChangeBirthday = (date: Date) => {
        onChange({ birthday: date })
    }

    return (
        <div className={`verify-registration ${className}`}>
            <h4 className="verify-registration__title">{trans('pages.registration.checkYourData')}</h4>
            <div className="verify-registration__modal-body">
                <p>
                    {trans('pages.registration.checkYourDataIntro1')}
                    <br />
                    {trans('pages.registration.checkYourDataIntro2')}
                </p>
                {values.employeeNumber
            && (
                <div className="verify-registration__row">
                    <div className="verify-registration__col">
                        <div className="form-group">
                            <label>{trans('common.employeeNumber')}</label>
                            <input
                                className={classNames('form-control', inputClassName)}
                                disabled
                                type="number"
                                name="employeeNumber"
                                id="employeeNumber"
                                autoComplete="off"
                                defaultValue={values.employeeNumber}
                            />
                        </div>
                    </div>
                </div>
            )}
                <div className="verify-registration__row">
                    <div className="verify-registration__col">
                        <div className="form-group">
                            <label>{trans('common.firstName')}</label>
                            <input
                                className={classNames('form-control', inputClassName, { 'is-invalid': !! validationErrors.firstname })}
                                type="text"
                                name="firstname"
                                id="firstname"
                                autoComplete="off"
                                defaultValue={values.firstname}
                                onChange={onChangeFirstname}
                            />
                            <div className="invalid-feedback">
                                { validationErrors.firstname }
                            </div>
                        </div>
                    </div>
                </div>
                <div className="verify-registration__row">
                    <div className="verify-registration__col">
                        <div className="form-group">
                            <label>{trans('common.affix')}</label>
                            <input
                                className={classNames('form-control', inputClassName)}
                                type="text"
                                name="affix"
                                id="affix"
                                autoComplete="off"
                                defaultValue={values.affix}
                                onChange={onChangeAffix}
                            />
                        </div>
                    </div>
                </div>
                <div className="verify-registration__row">
                    <div className="verify-registration__col">
                        <div className="form-group">
                            <label>{trans('common.lastName')}</label>
                            <input
                                className={classNames('form-control', inputClassName, { 'is-invalid': !! validationErrors.lastname })}
                                type="text"
                                name="lastname"
                                id="lastname"
                                autoComplete="off"
                                defaultValue={values.lastname}
                                onChange={onChangeLastname}
                            />
                            <div className="invalid-feedback">
                                { validationErrors.lastname }
                            </div>
                        </div>
                    </div>
                </div>
                <div className="verify-registration__row">
                    <div className="verify-registration__col">
                        <div className="form-group">
                            <label>{trans('common.email')}</label>
                            <input
                                className={classNames('form-control', inputClassName)}
                                disabled
                                type="email"
                                name="email"
                                id="email"
                                autoComplete="off"
                                defaultValue={values.email}
                            />
                            <div className="invalid-feedback">
                                { validationErrors.email }
                            </div>
                        </div>
                    </div>
                </div>
                <div className="verify-registration__row">
                    <div className="verify-registration__col">
                        <div className="form-group">
                            <label>{trans('common.dateOfBirth')}</label>
                            <FormDatePicker
                                showYearDropdown
                                selected={values.birthday}
                                onChange={onChangeBirthday}
                                datePickerClassName={inputClassName}
                            />
                        </div>
                    </div>
                </div>
                <div className="verify-registration__row">
                    <div className="verify-registration__col">
                        <div className="form-group">
                            <label>{trans('common.phone')}</label>
                            <input
                                className={classNames('form-control', inputClassName, { 'is-invalid': !! validationErrors.phone })}
                                type="tel"
                                pattern="^[0-9-+\s()]*$"
                                name="phone"
                                id="phone"
                                defaultValue={values.phone}
                                onChange={onChangePhone}
                            />
                            <div className="invalid-feedback">
                                { validationErrors.phone }
                            </div>
                        </div>
                    </div>
                </div>
                <PreviousAndNextButton onPreviousStep={onPreviousStep} onNextStep={onNextStepClick} />
            </div>
        </div>
    )
}
